import { useEffect, useMemo, useRef, useState } from "react";
import {
  Sparkles, ChevronRight, ChevronLeft, Check, Plus, Trash2,
  FileText, Download, Wand2, Save, Clock, FolderOpen, Upload, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { PageHeader, Spinner } from "@/components/UI";
import { formatCurrency, formatDate } from "@/lib/format";

/**
 * Bid Proposal Wizard – guided by the BidForms_Standard PPDA template.
 * On compile, the backend overlays the answers onto the standard 22-page
 * template (untouched fonts / colours / tables) and merges vault attachments.
 */

const STEPS = [
  "Bid",
  "Tender",
  "Signatory",
  "Price schedules",
  "Compliance",
  "National content & Owners",
  "Attachments & Compile",
];

// Stable client-side keys so React reconciliation survives splice/reorder
// on the wizard's list inputs (using array-index as key made input focus jump
// to the wrong row when the user deleted an item mid-list). Stripped in
// `compile()` before the payload hits the backend.
const newKey = () => `k_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
const stripKey = (arr) => (arr || []).map(({ _key, ...rest }) => rest);
const emptyItem = () => ({ _key: newKey(), description: "", unit_price: "", total_price: "" });
const emptySpec = () => ({ _key: newKey(), specification: "", compliance: "Comply", literature: "" });
const emptyOwner = () => ({ _key: newKey(), name: "", percentage: "", gender: "M" });

export default function Proposals() {
  const [bids, setBids] = useState([]);
  const [previous, setPrevious] = useState([]);
  const [defaults, setDefaults] = useState({ company: {}, vault: [] });

  const [step, setStep] = useState(0);
  const [selectedBid, setSelectedBid] = useState(null);
  const [wizard, setWizard] = useState({
    pde: "",
    pde_address: "",
    subject: "",
    ref_number: "",
    bid_date: new Date().toLocaleDateString("en-GB", {
      day: "2-digit", month: "long", year: "numeric",
    }),
    bid_validity: "",
    delivery_period: "14 days",
    currency: "UGX",
    signatory_name: "",
    signatory_position: "",
    bidder_company: "",
    bidder_address: "",
    national_content: {
      employment_and_training: "",
      local_procurement: "",
      goods_works_services: "",
    },
    beneficial_owners: [emptyOwner()],
    pep: { is_pep: false, name: "", office_position: "", office_start: "" },
    price_schedules: {
      year_1: { currency: "UGX", items: [emptyItem()] },
      year_2: { currency: "UGX", items: [emptyItem()] },
      year_3: { currency: "UGX", items: [emptyItem()] },
    },
    compliance_matrix: [emptySpec()],
    vault_document_ids: [],
  });
  const [compiling, setCompiling] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [draftLabel, setDraftLabel] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiUrl, setAiUrl] = useState("");
  const [aiRunning, setAiRunning] = useState(false);
  const fileRef = useRef(null);
  const { toast } = useToast();

  const loadDrafts = () =>
    api.wizardListDrafts().then(setDrafts).catch(() => {});

  useEffect(() => {
    api.listBids().then(setBids).catch(() => {});
    api.listProposals().then(setPrevious).catch(() => {});
    loadDrafts();
    api.wizardDefaults().then((d) => {
      setDefaults(d);
      setWizard((w) => ({
        ...w,
        bidder_company: d?.company?.legal_name || "Jero-One Technologies Limited",
        bidder_address: d?.company?.address || "",
        signatory_name: d?.company?.director || "",
        signatory_position: "Director",
      }));
    }).catch(() => {});
  }, []);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));
  const update = (patch) => setWizard((w) => ({ ...w, ...patch }));

  const pickBid = (b) => {
    setSelectedBid(b);
    update({
      pde: b.client_name || "",
      subject: b.title || "",
      ref_number: b.ref_number || "",
      currency: b.currency || "UGX",
    });
    next();
  };

  const setSchedule = (year, patch) => {
    setWizard((w) => ({
      ...w,
      price_schedules: {
        ...w.price_schedules,
        [year]: { ...w.price_schedules[year], ...patch },
      },
    }));
  };
  const setScheduleItem = (year, idx, patch) => {
    setWizard((w) => {
      const items = [...w.price_schedules[year].items];
      items[idx] = { ...items[idx], ...patch };
      return {
        ...w,
        price_schedules: {
          ...w.price_schedules,
          [year]: { ...w.price_schedules[year], items },
        },
      };
    });
  };
  const addItem = (year) =>
    setSchedule(year, {
      items: [...wizard.price_schedules[year].items, emptyItem()],
    });
  const removeItem = (year, idx) => {
    const items = wizard.price_schedules[year].items.filter((_, i) => i !== idx);
    setSchedule(year, { items: items.length ? items : [emptyItem()] });
  };

  const setSpec = (idx, patch) => {
    setWizard((w) => {
      const cm = [...w.compliance_matrix];
      cm[idx] = { ...cm[idx], ...patch };
      return { ...w, compliance_matrix: cm };
    });
  };
  const addSpec = () => update({ compliance_matrix: [...wizard.compliance_matrix, emptySpec()] });
  const removeSpec = (i) => {
    const cm = wizard.compliance_matrix.filter((_, x) => x !== i);
    update({ compliance_matrix: cm.length ? cm : [emptySpec()] });
  };

  const setOwner = (idx, patch) => {
    setWizard((w) => {
      const arr = [...w.beneficial_owners];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...w, beneficial_owners: arr };
    });
  };
  const addOwner = () => update({ beneficial_owners: [...wizard.beneficial_owners, emptyOwner()] });
  const removeOwner = (i) => {
    const arr = wizard.beneficial_owners.filter((_, x) => x !== i);
    update({ beneficial_owners: arr.length ? arr : [emptyOwner()] });
  };

  const toggleVault = (id) => {
    const ids = wizard.vault_document_ids.includes(id)
      ? wizard.vault_document_ids.filter((x) => x !== id)
      : [...wizard.vault_document_ids, id];
    update({ vault_document_ids: ids });
  };

  const totals = useMemo(() => {
    const num = (v) => Number(v) || 0;
    const sumYear = (y) =>
      wizard.price_schedules[y].items.reduce((s, i) => s + num(i.total_price), 0);
    return {
      year_1: sumYear("year_1"),
      year_2: sumYear("year_2"),
      year_3: sumYear("year_3"),
      grand: sumYear("year_1") + sumYear("year_2") + sumYear("year_3"),
    };
  }, [wizard.price_schedules]);

  // Return the wizard payload with the client-side `_key` stripped from all
  // list-item arrays. Backend never sees these render-only identifiers.
  const cleanWizard = () => ({
    ...wizard,
    beneficial_owners: stripKey(wizard.beneficial_owners),
    compliance_matrix: stripKey(wizard.compliance_matrix),
    price_schedules: {
      year_1: { ...wizard.price_schedules.year_1, items: stripKey(wizard.price_schedules.year_1?.items) },
      year_2: { ...wizard.price_schedules.year_2, items: stripKey(wizard.price_schedules.year_2?.items) },
      year_3: { ...wizard.price_schedules.year_3, items: stripKey(wizard.price_schedules.year_3?.items) },
    },
  });

  const compile = async () => {
    setCompiling(true);
    try {
      const rec = await api.wizardCompile(cleanWizard());
      toast(`Compiled ${(rec.size / 1048576).toFixed(1)} MB — downloading…`, "success");
      const res = await api.wizardCompiledDownload(rec.proposal_id);
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = rec.filename || "Compiled_Bid.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      api.listProposals().then(setPrevious);
    } catch (e) {
      toast(e?.response?.data?.detail || "Compile failed", "error");
    } finally {
      setCompiling(false);
    }
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const saved = await api.wizardSaveDraft({
        id: draftId || undefined,
        label: draftLabel || wizard.subject || wizard.ref_number || "Untitled draft",
        step,
        data: cleanWizard(),
      });
      setDraftId(saved.id);
      setDraftLabel(saved.label);
      setLastSavedAt(saved.updated_at);
      loadDrafts();
      toast("Draft saved", "success");
    } catch (e) {
      toast(e?.response?.data?.detail || "Save failed", "error");
    } finally {
      setSavingDraft(false);
    }
  };

  // Hydrate arrays coming from backend/AI so they get client-side _key values
  const withKeys = (arr, factory) =>
    (arr || []).map((row) => ({ ...factory(), ...row, _key: row._key || newKey() }));

  const resumeDraft = async (id) => {
    try {
      const d = await api.wizardLoadDraft(id);
      const data = d.data || {};
      // Re-attach _key to lists so keyed rendering + splice stays stable
      if (data.beneficial_owners) data.beneficial_owners = withKeys(data.beneficial_owners, emptyOwner);
      if (data.compliance_matrix) data.compliance_matrix = withKeys(data.compliance_matrix, emptySpec);
      if (data.price_schedules) {
        ["year_1", "year_2", "year_3"].forEach((y) => {
          if (data.price_schedules[y]?.items) {
            data.price_schedules[y].items = withKeys(data.price_schedules[y].items, emptyItem);
          }
        });
      }
      setWizard((w) => ({ ...w, ...data }));
      setDraftId(d.id);
      setDraftLabel(d.label || "");
      setStep(Number(d.step) || 1);
      setLastSavedAt(d.updated_at);
      toast(`Resumed "${d.label || "draft"}"`, "success");
    } catch (e) {
      toast(e?.response?.data?.detail || "Load failed", "error");
    }
  };

  const removeDraft = async (id, ev) => {
    ev?.stopPropagation?.();
    if (!window.confirm("Delete this draft?")) return;
    try {
      await api.wizardDeleteDraft(id);
      if (id === draftId) {
        setDraftId(null);
        setDraftLabel("");
      }
      loadDrafts();
      toast("Draft deleted", "success");
    } catch (e) {
      toast(e?.response?.data?.detail || "Delete failed", "error");
    }
  };

  const runAiPrefill = async (source) => {
    setAiRunning(true);
    try {
      const args = {};
      if (source === "text") args.text = aiText;
      else if (source === "url") args.url = aiUrl;
      else if (source === "file") args.file = fileRef.current?.files?.[0];
      const parsed = await api.wizardAiPrefill(args);
      // Merge into wizard, only overwriting empty fields
      setWizard((w) => ({
        ...w,
        source_document_id: parsed.source_document_id || w.source_document_id || "",
        pde: w.pde || parsed.pde || "",
        pde_address: w.pde_address || parsed.pde_address || "",
        subject: w.subject || parsed.subject || "",
        ref_number: w.ref_number || parsed.ref_number || "",
        bid_date: w.bid_date || parsed.bid_date || w.bid_date,
        bid_validity: w.bid_validity || parsed.bid_validity || "",
        delivery_period: w.delivery_period || parsed.delivery_period || w.delivery_period,
        currency: w.currency || parsed.currency || "UGX",
        // Merge compliance rows: replace if wizard has only the initial empty row
        compliance_matrix:
          w.compliance_matrix?.length === 1 && !w.compliance_matrix[0].specification
            ? parsed.compliance_matrix?.length
              ? withKeys(parsed.compliance_matrix, emptySpec)
              : w.compliance_matrix
            : [...w.compliance_matrix, ...withKeys(parsed.compliance_matrix, emptySpec)],
      }));
      const n = parsed.compliance_matrix?.length || 0;
      toast(`AI pre-filled${n ? ` · ${n} compliance rows drafted` : ""}`, "success");
      setAiOpen(false);
      setAiText("");
      setAiUrl("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast(e?.response?.data?.detail || "AI extraction failed", "error");
    } finally {
      setAiRunning(false);
    }
  };

  const reset = () => {
    setStep(0);
    setDraftId(null);
    setDraftLabel("");
    setLastSavedAt(null);
    setSelectedBid(null);
  };

  return (
    <div data-testid="page-proposals">
      <PageHeader
        icon={Sparkles}
        title="Bid Proposal Wizard"
        subtitle="Guided by the PPDA Standard Bid Form · Outputs a branded, template-preserved PDF"
        action={
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={saveDraft}
                disabled={savingDraft}
                className="btn btn-ghost"
                data-testid="save-draft-btn"
                title={lastSavedAt ? `Last saved ${formatDate(lastSavedAt)}` : "Save draft"}
              >
                {savingDraft ? <Spinner /> : <Save size={14} />}
                &nbsp;{draftId ? "Update draft" : "Save draft"}
              </button>
            )}
            <button onClick={reset} className="btn btn-ghost" data-testid="reset-wizard">Start over</button>
          </div>
        }
      />

      {draftId && step > 0 && (
        <div className="text-xs muted mb-3 flex items-center gap-2" data-testid="draft-status">
          <Clock size={12} />
          Editing draft: <b>{draftLabel}</b>
          {lastSavedAt && <span> · saved {formatDate(lastSavedAt)}</span>}
        </div>
      )}

      {/* Stepper */}
      <div className="card card-tight mb-5">
        <div className="flex items-center gap-2 overflow-x-auto">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1 min-w-[110px]">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                style={{
                  background:
                    i < step ? "var(--green-700)" :
                    i === step ? "var(--green-600)" : "var(--ink-300)",
                }}
              >
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <div className={`text-xs font-semibold ${i === step ? "" : "muted"}`}>{s}</div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
            </div>
          ))}
        </div>
      </div>

      {/* Step 0 – Pick bid + Resume drafts */}
      {step === 0 && (
        <>
          {drafts.length > 0 && (
            <div className="card card-padded mb-5" data-testid="drafts-panel">
              <h3 className="font-bold mb-1 flex items-center gap-2">
                <FolderOpen size={16} /> Resume a draft
              </h3>
              <p className="text-xs muted mb-3">
                Continue where you left off. Drafts are auto-labelled from the tender subject.
              </p>
              <div className="space-y-2 max-h-[260px] overflow-y-auto">
                {drafts.map((d) => (
                  <div
                    key={d.id}
                    className="border border-slate-100 hover:border-[#0e6939] rounded-lg p-3 flex items-center justify-between gap-3 transition"
                  >
                    <button
                      onClick={() => resumeDraft(d.id)}
                      className="flex-1 min-w-0 text-left"
                      data-testid={`draft-resume-${d.id}`}
                    >
                      <div className="font-semibold text-sm truncate">{d.label}</div>
                      <div className="text-xs muted truncate">
                        Step {(Number(d.step) || 0) + 1} / {STEPS.length} · saved {formatDate(d.updated_at)}
                      </div>
                    </button>
                    <button
                      onClick={(ev) => removeDraft(d.id, ev)}
                      className="btn btn-ghost btn-icon"
                      data-testid={`draft-delete-${d.id}`}
                      aria-label="Delete draft"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="card card-padded">
            <h3 className="font-bold mb-1">Choose a bid</h3>
          <p className="text-xs muted mb-4">
            Fields will be pre-filled from the selected bid. All are editable in the next steps.
          </p>
          <div className="space-y-2 max-h-[480px] overflow-y-auto">
            {bids.map((b) => (
              <button
                key={b.id}
                data-testid={`pick-${b.id}`}
                onClick={() => pickBid(b)}
                className="w-full text-left border border-slate-100 hover:border-[#0e6939] rounded-lg p-3 transition"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{b.title}</div>
                    <div className="text-xs muted truncate">{b.client_name} · {b.ref_number || "—"}</div>
                  </div>
                  <div className="text-xs font-semibold whitespace-nowrap">
                    {formatCurrency(b.bid_amount, b.currency)}
                  </div>
                </div>
              </button>
            ))}
            {bids.length === 0 && (
              <div className="text-sm muted p-6 text-center">
                No bids yet. Add a bid first from the Bids page.
              </div>
            )}
          </div>
        </div>
        </>
      )}

      {/* Step 1 – Tender details */}
      {step === 1 && (
        <div className="card card-padded space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-bold">Tender details</h3>
            <button
              onClick={() => setAiOpen(true)}
              className="btn btn-ghost"
              data-testid="ai-prefill-btn"
              title="Extract fields from a tender PDF / URL / pasted text"
            >
              <Wand2 size={14} /> AI Pre-fill
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Procuring & Disposing Entity (PDE)" testId="fld-pde"
              value={wizard.pde} onChange={(v) => update({ pde: v })} />
            <Field label="PDE Address" testId="fld-pde-addr"
              value={wizard.pde_address} onChange={(v) => update({ pde_address: v })} />
            <Field label="Subject of Procurement" testId="fld-subject" cols={2}
              value={wizard.subject} onChange={(v) => update({ subject: v })} />
            <Field label="Procurement Reference Number" testId="fld-ref"
              value={wizard.ref_number} onChange={(v) => update({ ref_number: v })} />
            <Field label="Bid Date" testId="fld-date"
              value={wizard.bid_date} onChange={(v) => update({ bid_date: v })} />
            <Field label="Bid Validity Date" testId="fld-validity"
              value={wizard.bid_validity} onChange={(v) => update({ bid_validity: v })}
              hint="e.g. 12 September 2026 (typically 120 days from bid date)" />
            <Field label="Delivery Period" testId="fld-delivery"
              value={wizard.delivery_period} onChange={(v) => update({ delivery_period: v })}
              hint="e.g. 14 days / 3 weeks / 2 months" />
            <Field label="Currency" testId="fld-currency"
              value={wizard.currency} onChange={(v) => update({ currency: v })}
              hint="UGX / USD / EUR" />
          </div>
          <NavRow onPrev={prev} onNext={next} />
        </div>
      )}

      {/* Step 2 – Signatory */}
      {step === 2 && (
        <div className="card card-padded space-y-4">
          <h3 className="font-bold">Authorised signatory</h3>
          <p className="text-xs muted">
            Applied to every signature block across the 22 template pages.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Signatory Name" testId="fld-sig-name"
              value={wizard.signatory_name} onChange={(v) => update({ signatory_name: v })} />
            <Field label="Position / Capacity" testId="fld-sig-pos"
              value={wizard.signatory_position} onChange={(v) => update({ signatory_position: v })} />
            <Field label="Company Legal Name" testId="fld-sig-company" cols={2}
              value={wizard.bidder_company} onChange={(v) => update({ bidder_company: v })} />
            <Field label="Company Address" testId="fld-sig-addr" cols={2}
              value={wizard.bidder_address} onChange={(v) => update({ bidder_address: v })} />
          </div>
          <NavRow onPrev={prev} onNext={next} />
        </div>
      )}

      {/* Step 3 – Price schedules */}
      {step === 3 && (
        <div className="card card-padded space-y-6">
          <h3 className="font-bold">Price schedules (Year 1 / 2 / 3)</h3>
          <p className="text-xs muted">
            Unit and total prices are overlaid on Pages 15-17 of the standard bid form.
          </p>
          {["year_1", "year_2", "year_3"].map((y) => (
            <ScheduleTable
              key={y}
              label={`Year ${y.split("_")[1]}`}
              testId={`sched-${y}`}
              schedule={wizard.price_schedules[y]}
              onCurrency={(v) => setSchedule(y, { currency: v })}
              onItem={(idx, patch) => setScheduleItem(y, idx, patch)}
              onAdd={() => addItem(y)}
              onRemove={(idx) => removeItem(y, idx)}
              subtotal={totals[y]}
            />
          ))}
          <div className="rounded-lg bg-slate-50 p-4 flex items-center justify-between">
            <div className="text-sm font-semibold">Grand Total (3-year)</div>
            <div className="text-lg font-bold" style={{ color: "var(--green-700)" }}>
              {formatCurrency(totals.grand, wizard.currency)}
            </div>
          </div>
          <NavRow onPrev={prev} onNext={next} />
        </div>
      )}

      {/* Step 4 – Compliance matrix */}
      {step === 4 && (
        <div className="card card-padded space-y-4">
          <h3 className="font-bold">Specification & Compliance Matrix</h3>
          <p className="text-xs muted">
            Each row is appended after the Standard template as the Compliance
            Response Matrix (matches SBD_00093 sample layout).
          </p>
          <div className="space-y-3">
            {wizard.compliance_matrix.map((r, i) => (
              <div
                key={r._key || i}
                data-testid={`spec-row-${i}`}
                className="grid md:grid-cols-[1fr_140px_180px_36px] gap-3 items-start"
              >
                <textarea
                  rows={2}
                  className="textarea text-sm"
                  placeholder={`Specification #${i + 1}`}
                  value={r.specification}
                  onChange={(e) => setSpec(i, { specification: e.target.value })}
                  data-testid={`spec-${i}-text`}
                />
                <select
                  className="input text-sm"
                  value={r.compliance}
                  onChange={(e) => setSpec(i, { compliance: e.target.value })}
                  data-testid={`spec-${i}-comply`}
                >
                  <option value="Comply">Comply</option>
                  <option value="Not Comply">Not Comply</option>
                  <option value="Partial">Partial</option>
                </select>
                <input
                  className="input text-sm"
                  placeholder="Tech literature ref"
                  value={r.literature}
                  onChange={(e) => setSpec(i, { literature: e.target.value })}
                  data-testid={`spec-${i}-lit`}
                />
                <button
                  onClick={() => removeSpec(i)}
                  className="btn btn-ghost btn-icon"
                  data-testid={`spec-${i}-remove`}
                  aria-label="Remove row"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={addSpec} className="btn btn-ghost" data-testid="spec-add">
            <Plus size={14} /> Add specification
          </button>
          <NavRow onPrev={prev} onNext={next} />
        </div>
      )}

      {/* Step 5 – National Content & Beneficial Owners */}
      {step === 5 && (
        <div className="card card-padded space-y-6">
          <div>
            <h3 className="font-bold mb-3">National Content Responses</h3>
            <div className="space-y-3">
              <NcField
                label="1. Employment and training of Ugandans"
                testId="nc-emp"
                value={wizard.national_content.employment_and_training}
                onChange={(v) => update({
                  national_content: { ...wizard.national_content, employment_and_training: v },
                })}
              />
              <NcField
                label="2. Procurement of goods and services obtainable in Uganda"
                testId="nc-proc"
                value={wizard.national_content.local_procurement}
                onChange={(v) => update({
                  national_content: { ...wizard.national_content, local_procurement: v },
                })}
              />
              <NcField
                label="3. Goods, works and services provided by Ugandan companies/citizens"
                testId="nc-goods"
                value={wizard.national_content.goods_works_services}
                onChange={(v) => update({
                  national_content: { ...wizard.national_content, goods_works_services: v },
                })}
              />
            </div>
          </div>

          <div>
            <h3 className="font-bold mb-3">Beneficial Owners</h3>
            <div className="space-y-3">
              {wizard.beneficial_owners.map((o, i) => (
                <div key={o._key || i} className="grid md:grid-cols-[1fr_120px_100px_36px] gap-3 items-center" data-testid={`owner-${i}`}>
                  <input
                    className="input text-sm"
                    placeholder="Full name"
                    value={o.name}
                    onChange={(e) => setOwner(i, { name: e.target.value })}
                    data-testid={`owner-${i}-name`}
                  />
                  <input
                    className="input text-sm"
                    placeholder="Percentage"
                    value={o.percentage}
                    onChange={(e) => setOwner(i, { percentage: e.target.value })}
                    data-testid={`owner-${i}-pct`}
                  />
                  <select
                    className="input text-sm"
                    value={o.gender}
                    onChange={(e) => setOwner(i, { gender: e.target.value })}
                    data-testid={`owner-${i}-gender`}
                  >
                    <option value="M">M</option>
                    <option value="F">F</option>
                  </select>
                  <button onClick={() => removeOwner(i)} className="btn btn-ghost btn-icon" aria-label="Remove owner">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={addOwner} className="btn btn-ghost mt-2" data-testid="owner-add">
              <Plus size={14} /> Add owner
            </button>

            <label className="flex items-center gap-3 text-sm cursor-pointer mt-4">
              <input
                type="checkbox"
                checked={wizard.pep.is_pep}
                onChange={(e) => update({ pep: { ...wizard.pep, is_pep: e.target.checked } })}
                data-testid="pep-toggle"
              />
              <span>Any beneficial owner is a Politically Exposed Person (PEP)</span>
            </label>
            {wizard.pep.is_pep && (
              <div className="grid md:grid-cols-3 gap-3 mt-2">
                <input
                  className="input text-sm"
                  placeholder="PEP name"
                  value={wizard.pep.name}
                  onChange={(e) => update({ pep: { ...wizard.pep, name: e.target.value } })}
                />
                <input
                  className="input text-sm"
                  placeholder="Public office position"
                  value={wizard.pep.office_position}
                  onChange={(e) => update({ pep: { ...wizard.pep, office_position: e.target.value } })}
                />
                <input
                  className="input text-sm"
                  placeholder="Date office assumed"
                  value={wizard.pep.office_start}
                  onChange={(e) => update({ pep: { ...wizard.pep, office_start: e.target.value } })}
                />
              </div>
            )}
          </div>

          <NavRow onPrev={prev} onNext={next} />
        </div>
      )}

      {/* Step 6 – Attachments + Compile */}
      {step === 6 && (
        <div className="card card-padded space-y-5">
          <div>
            <h3 className="font-bold mb-1">Vault attachments</h3>
            <p className="text-xs muted mb-3">
              Selected PDFs are appended after the standard forms (CVs,
              certificates, datasheets, legal docs). Order follows the vault list.
            </p>
            <div
              className="max-h-[320px] overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100"
              data-testid="vault-picker"
            >
              {defaults.vault.length === 0 && (
                <div className="p-4 text-xs muted">
                  No documents in your Vault yet. Upload PDFs (Certificates, CVs,
                  Trade Licence, URA-TCC, NSSF, etc.) from the Vault page first.
                </div>
              )}
              {defaults.vault.map((d) => (
                <label
                  key={d.id}
                  className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={wizard.vault_document_ids.includes(d.id)}
                    onChange={() => toggleVault(d.id)}
                    data-testid={`vault-${d.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs truncate">{d.title}</div>
                    <div className="text-[11px] muted">
                      {d.category || "Certificate"}
                      {d.expiry_date && ` · Expires ${d.expiry_date}`}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className={`rounded-lg border p-4 ${wizard.source_document_id ? "border-[#0e6939] bg-[#f3faf4]" : "border-amber-400 bg-amber-50"}`}>
            <div className="flex items-start gap-3">
              <Wand2 size={18} style={{ color: wizard.source_document_id ? "var(--green-700)" : "#b45309" }} />
              <div className="text-sm flex-1">
                {wizard.source_document_id ? (
                  <>
                    <div className="font-semibold">Ready to compile</div>
                    <div className="text-xs muted mt-1" data-testid="compile-ready-note">
                      Covering Letter → the client's OWN bidding forms extracted from the
                      purchased document (letterheaded, blanks filled, signed &amp; stamped)
                      → {wizard.vault_document_ids.length} vault attachment(s) in PPDA order.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold text-amber-800">Purchased bid document required</div>
                    <div className="text-xs text-amber-700 mt-1" data-testid="compile-missing-source">
                      Upload the purchased bid document via <b>AI Prefill → Upload file</b> (Step 1)
                      first. The wizard extracts the client's own Bid Submission Sheet and forms —
                      it never uses a generic template.
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={prev} disabled={compiling} className="btn btn-ghost">
              <ChevronLeft size={14} /> Back
            </button>
            <button
              onClick={compile}
              disabled={compiling}
              className="btn btn-primary btn-lg"
              data-testid="compile-btn"
            >
              {compiling ? (<><Spinner /> &nbsp;Compiling…</>) : (<><Download size={14} /> Compile PDF</>)}
            </button>
          </div>
        </div>
      )}

      {/* Previous proposals footer */}
      {previous.length > 0 && step === 0 && (
        <div className="card card-padded mt-6">
          <h3 className="font-bold mb-3 flex items-center gap-2">
            <FileText size={16} /> Previous proposals
          </h3>
          <div className="divide-y divide-slate-100">
            {previous.slice(0, 5).map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between py-3 px-2 text-left"
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {p.request?.subject || p.request?.bid_title || "Proposal"}
                  </div>
                  <div className="text-xs muted truncate">
                    {p.request?.pde || p.request?.client_name || ""} · {formatDate(p.created_at)}
                  </div>
                </div>
                <span className="text-xs font-semibold" style={{ color: "var(--green-700)" }}>
                  {p.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI pre-fill modal */}
      {aiOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          role="dialog"
          data-testid="ai-prefill-modal"
          onClick={(e) => e.target === e.currentTarget && !aiRunning && setAiOpen(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Wand2 size={16} style={{ color: "var(--green-700)" }} />
                <h3 className="font-bold">AI Pre-fill from tender document</h3>
              </div>
              <button
                onClick={() => !aiRunning && setAiOpen(false)}
                className="btn btn-ghost btn-icon"
                aria-label="Close"
                data-testid="ai-prefill-close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs muted">
                Paste the tender text, share the URL, or upload the RFP PDF —
                Gemini extracts PDE, subject, ref, delivery period, currency,
                and drafts a compliance-matrix skeleton. Existing values are
                preserved.
              </p>

              <div>
                <label className="label">Upload tender PDF</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,application/pdf,text/plain"
                  className="input"
                  data-testid="ai-prefill-file"
                />
                <button
                  onClick={() => runAiPrefill("file")}
                  disabled={aiRunning}
                  className="btn btn-primary btn-sm mt-2"
                  data-testid="ai-prefill-file-run"
                >
                  {aiRunning ? <Spinner /> : <Upload size={12} />} Extract from PDF
                </button>
              </div>

              <div className="border-t border-slate-100 pt-3">
                <label className="label">Or paste tender text</label>
                <textarea
                  rows={5}
                  className="textarea text-sm"
                  placeholder="Paste tender / RFP / bid-notice text..."
                  value={aiText}
                  onChange={(e) => setAiText(e.target.value)}
                  data-testid="ai-prefill-text"
                />
                <button
                  onClick={() => runAiPrefill("text")}
                  disabled={aiRunning || aiText.trim().length < 30}
                  className="btn btn-primary btn-sm mt-2"
                  data-testid="ai-prefill-text-run"
                >
                  {aiRunning ? <Spinner /> : <Wand2 size={12} />} Extract from text
                </button>
              </div>

              <div className="border-t border-slate-100 pt-3">
                <label className="label">Or tender URL</label>
                <input
                  className="input"
                  placeholder="https://egpuganda.go.ug/notices/..."
                  value={aiUrl}
                  onChange={(e) => setAiUrl(e.target.value)}
                  data-testid="ai-prefill-url"
                />
                <button
                  onClick={() => runAiPrefill("url")}
                  disabled={aiRunning || !aiUrl.startsWith("http")}
                  className="btn btn-primary btn-sm mt-2"
                  data-testid="ai-prefill-url-run"
                >
                  {aiRunning ? <Spinner /> : <Wand2 size={12} />} Fetch & Extract
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- little UI helpers (kept in-file to avoid new components) ---------

function Field({ label, value, onChange, testId, cols = 1, hint }) {
  return (
    <div className={cols === 2 ? "md:col-span-2" : ""}>
      <label className="label">{label}</label>
      <input
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
      {hint && <p className="text-[11px] muted mt-1">{hint}</p>}
    </div>
  );
}

function NcField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="label">{label}</label>
      <textarea
        className="textarea"
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </div>
  );
}

function NavRow({ onPrev, onNext }) {
  return (
    <div className="flex justify-between pt-2">
      <button onClick={onPrev} className="btn btn-ghost" data-testid="wiz-prev">
        <ChevronLeft size={14} /> Back
      </button>
      <button onClick={onNext} className="btn btn-primary" data-testid="wiz-next">
        Continue <ChevronRight size={14} />
      </button>
    </div>
  );
}

function ScheduleTable({
  label, testId, schedule, onCurrency, onItem, onAdd, onRemove, subtotal,
}) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm">{label}</div>
        <div className="flex items-center gap-2 text-xs">
          <span className="muted">Currency:</span>
          <input
            className="input w-24 text-sm"
            value={schedule.currency}
            onChange={(e) => onCurrency(e.target.value)}
            data-testid={`${testId}-cur`}
          />
        </div>
      </div>
      <div className="space-y-2">
        {schedule.items.map((it, i) => (
          <div key={it._key || i} className="grid md:grid-cols-[1fr_140px_140px_36px] gap-2 items-center">
            <input
              className="input text-sm"
              placeholder={`Description of supplies #${i + 1}`}
              value={it.description}
              onChange={(e) => onItem(i, { description: e.target.value })}
              data-testid={`${testId}-desc-${i}`}
            />
            <input
              className="input text-sm text-right"
              placeholder="Unit price"
              value={it.unit_price}
              onChange={(e) => onItem(i, {
                unit_price: e.target.value,
                total_price: e.target.value, // 1 unit each on the template
              })}
              data-testid={`${testId}-unit-${i}`}
            />
            <input
              className="input text-sm text-right"
              placeholder="Total price"
              value={it.total_price}
              onChange={(e) => onItem(i, { total_price: e.target.value })}
              data-testid={`${testId}-total-${i}`}
            />
            <button
              onClick={() => onRemove(i)}
              className="btn btn-ghost btn-icon"
              aria-label="Remove item"
              data-testid={`${testId}-rm-${i}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-2">
        <button onClick={onAdd} className="btn btn-ghost btn-sm" data-testid={`${testId}-add`}>
          <Plus size={12} /> Add item
        </button>
        <div className="text-sm">
          Subtotal: <b>{formatCurrency(subtotal, schedule.currency)}</b>
        </div>
      </div>
    </div>
  );
}
